import { AppResourcePage } from './app-resource-base';
import { branchSwitchOnResource } from './helpers/branch-switch';
import { cache } from '../cache-impl/cache-registry';
import { checkSuppliersIsOnline } from '../quotes/quote-creator';
import { containsFlag, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { createConcurrencyCheck, ResourceLocker } from '../common/resource-lock';
import { createQuoteView, getQuoteContainer, getQuoteContainerManager, QuoteLock } from '../quotes/quote-ui-adapter';
import { customElement } from 'lit/decorators.js';
import { EAbort } from '../../../webmodule-common/other/abort';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import { getQuoteTitleStr } from '../../quotes/data/quote-helper-functions';
import { goStaticURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { LangAppQuotePage } from '../../../localization/scripts/dealer-franchisee/pages/app-quote-lang';
import { ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { NullPromise } from '../../../webmodule-common/interop/types/misc';
import { QuoteState } from '../../api/dealer-api-interface-quote';
import { v6BootLoader } from '../../v6config/v6config';

@customElement('app-quote')
export class AppQuotePage extends AppResourcePage {
  qm?: FranchiseeQuoteContainerManager;

  async createModalEditor(id: string): NullPromise<ModalViewBase> {
    if (!(await v6BootLoader())) {
      await information(LangAppQuotePage.supplierOfflineMsg());
      this.forcedReadonly = true;
      this.forcedReadonlyMsg = LangAppQuotePage.supplierOffline();
    }

    const container = getQuoteContainer(id);
    if (isEmptyOrSpace(id)) id = container.quoteId;

    this.qm = getQuoteContainerManager(container, this.lock) as FranchiseeQuoteContainerManager;
    const qm = this.qm;
    if (!(await qm.needsQuote())) return null;
    if (containsFlag(qm.quote.state, QuoteState.Copying)) {
      await information(LangAppQuotePage.cannotOpenWhileCopying());
      throw new EAbort();
    }

    // Expose currency to globalThis for external libraries (V6 Configurator)
    (globalThis as any).externalLibraryCurrency.currency = qm.quoteCurrency;

    if (!(await branchSwitchOnResource(qm.branchQuote.branchId, LangAppQuotePage.quoteGlossary()))) return null;
    if (!(await checkSuppliersIsOnline(this.qm.quote.supplierId))) {
      await information(LangAppQuotePage.supplierOfflineMsg());
      this.forcedReadonly = true;
      this.forcedReadonlyMsg = LangAppQuotePage.supplierOffline();
    }
    this.qm.forceLockout = this.forcedReadonly;

    return await createQuoteView(qm);
  }

  async redirectPath(): NullPromise<string> {
    return '/quotes';
  }

  meta() {
    return {
      title: () => LangAppQuotePage.quoteEditorTitle({ quoteTitle: getQuoteTitleStr(this.qm?.quote, undefined, true) }),
      description: 'SoftTech dealer CPQ'
    };
  }

  protected async resourceRefreshing() {
    await super.resourceRefreshing();
    if (this.qm?.quote.id) {
      await cache().projectResourceLink.updateLocal(this.qm?.quote.id);
      await cache().quote.updateLocal(this.qm?.quote.id);
    }
  }

  protected createLock(id: string): ResourceLocker | undefined {
    const lockRevokedEvent = async () => {
      await this.view?.abandonAndClose(true);
      goStaticURL('/quotes');
    };

    // Create concurrency check that ensures local data is loaded and compares recordVersion with server
    const concurrencyCheck = createConcurrencyCheck('QUOTE.QUOTE', id, async () => {
      await this.qm?.needsQuote();
      return this.qm?.quote?.recordVersion ?? '';
    });

    return new QuoteLock(id, lockRevokedEvent, concurrencyCheck);
  }
}
